import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Sparkles, X } from 'lucide-react';
import { Link, Head } from '@inertiajs/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Component Imports
import ContactUs from '../../Components/ContactUs';
import Footer from '../../Components/Footer';
import FloatingMenu from '../../Components/FloatingMenu';
import SmoothScroll from '../../Components/SmoothScroll';
import HorizontalAmenities from '../../Components/HorizontalAmenities';
import StandardHeroVideo from '../../Components/StandardHeroVideo';
import SeniorLivingSection from '../../Components/SeniorLivingSection';
import WhyChooseUs from '../../Components/WhyChooseUs';
import MenuOverlay from '../../Components/MenuOverlay';

// Re-register GSAP plugins
if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

function Home({ blogs = [] }) {
    const containerRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [isScrolled, setIsScrolled] = useState(false);
    const bgRef = useRef(null);

    // Image modal state
    const [modalImage, setModalImage] = useState(null);
    const [modalInfo, setModalInfo] = useState({ title: '', description: '' });

    // Refs for horizontal scroll
    const horizontalWrapper1Ref = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeftState, setScrollLeftState] = useState(0);

    // Parallax effect for hero background
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleMouseMove = (e) => {
            const x = (e.clientX / window.innerWidth) * 10;
            const y = (e.clientY / window.innerHeight) * 10;
            const bg = container.querySelector('.background-image');
            if (bg) {
                bg.style.transform = `scale(1.1) translate(-${x}px, -${y}px)`;
            }
        };

        container.addEventListener('mousemove', handleMouseMove);
        return () => container.removeEventListener('mousemove', handleMouseMove);
    }, []);

    // Scroll progress and background toggle
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        const handleScroll = () => {
            const scrollTop = scrollContainer.scrollTop;
            const scrollHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;
            const progress = Math.min(scrollTop / (scrollHeight * 0.3), 1);
            setScrollProgress(progress);

            if (scrollTop > 50) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        scrollContainer.addEventListener('scroll', handleScroll);
        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, []);

    // Modal Handlers
    const openImageModal = (imageSrc, title, description) => {
        setModalImage(imageSrc);
        setModalInfo({ title, description });
        document.body.style.overflow = 'hidden';
    };

    const closeImageModal = () => {
        setModalImage(null);
        setModalInfo({ title: '', description: '' });
        document.body.style.overflow = '';
    };

    // Drag to scroll logic
    const handleMouseDown = (e) => {
        setIsDragging(true);
        if (horizontalWrapper1Ref.current) {
            setStartX(e.pageX - horizontalWrapper1Ref.current.offsetLeft);
            setScrollLeftState(horizontalWrapper1Ref.current.scrollLeft);
        }
    };

    const handleMouseLeave = () => setIsDragging(false);
    const handleMouseUp = () => setIsDragging(false);

    const handleMouseMoveDrag = (e) => {
        if (!isDragging || !horizontalWrapper1Ref.current) return;
        e.preventDefault();
        const x = e.pageX - horizontalWrapper1Ref.current.offsetLeft;
        const walk = (x - startX) * 2;
        horizontalWrapper1Ref.current.scrollLeft = scrollLeftState - walk;
    };

    const scrollLeft = () => {
        if (horizontalWrapper1Ref.current) {
            const card = horizontalWrapper1Ref.current.querySelector('.horizontal-card');
            const cardWidth = card ? card.offsetWidth + 50 : 600;
            horizontalWrapper1Ref.current.scrollBy({ left: -cardWidth, behavior: 'smooth' });
        }
    };

    const scrollRight = () => {
        if (horizontalWrapper1Ref.current) {
            const card = horizontalWrapper1Ref.current.querySelector('.horizontal-card');
            const cardWidth = card ? card.offsetWidth + 50 : 600;
            horizontalWrapper1Ref.current.scrollBy({ left: cardWidth, behavior: 'smooth' });
        }
    };

    return (
        <SmoothScroll ref={scrollContainerRef}>
            <Head>
                <title>Luxury Senior Living Hollywood - Hollywood Royale Garden</title>
                <meta name="description" content="Hollywood Royale Garden offers premium assisted living and senior care in the heart of Hollywood. Discover our luxury amenities, spacious rooms, and dedicated care." />
                <meta name="keywords" content="senior living Hollywood, assisted living Los Angeles, luxury retirement home, Hollywood Royale Garden, senior care Hollywood" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Luxury Senior Living Hollywood - Hollywood Royale Garden" />
                <meta property="og:description" content="Premium assisted living and senior care in Hollywood. Luxury amenities and professional care in a beautiful environment." />
                <meta property="og:image" content="/logo.png" />

                {/* Twitter */}
                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:title" content="Luxury Senior Living Hollywood - Hollywood Royale Garden" />
                <meta property="twitter:description" content="Premium assisted living and senior care in Hollywood. Luxury amenities and professional care." />
                <meta property="twitter:image" content="/logo.png" />

                {/* Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "AssistedLivingFacility",
                        "name": "Hollywood Royale Garden",
                        "image": "https://hrgalla.com/logo.png",
                        "@id": "https://hrgalla.com",
                        "url": "https://hrgalla.com",
                        "telephone": "+1-323-466-2411",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "6054 Franklin Ave",
                            "addressLocality": "Los Angeles",
                            "addressRegion": "CA",
                            "postalCode": "90028",
                            "addressCountry": "US"
                        },
                        "geo": {
                            "@type": "GeoCoordinates",
                            "latitude": 34.105459,
                            "longitude": -118.322046
                        },
                        "openingHoursSpecification": {
                            "@type": "OpeningHoursSpecification",
                            "dayOfWeek": [
                                "Monday",
                                "Tuesday",
                                "Wednesday",
                                "Thursday",
                                "Friday",
                                "Saturday",
                                "Sunday"
                            ],
                            "opens": "00:00",
                            "closes": "23:59"
                        }
                    })}
                </script>
            </Head>

            <FloatingMenu
                isVisible={true}
                isScrolled={isScrolled}
                toggleMenu={() => setIsMenuOpen(!isMenuOpen)}
                isMenuOpen={isMenuOpen}
            />

            <div className="landing-container" ref={containerRef}>
                <div className={`brand-box ${!isScrolled || isMenuOpen ? 'visible' : 'hidden'}`} onClick={() => window.location.href = '/'}>
                    <img src="/logo.png" alt="Hollywood Royale" className="brand-image" />
                </div>

                <MenuOverlay isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />

                {/* 360 Video Background */}
                <StandardHeroVideo scrollProgress={scrollProgress} />

                <div className="overlay" style={{ opacity: 1 - scrollProgress, pointerEvents: 'none', zIndex: 2 }}></div>

                <main className="main-content" style={{ opacity: 1 - scrollProgress, pointerEvents: 'none', zIndex: 10 }}>
                    <div className="hero-text">
                        {/* <h1>Live elevated<br />at Hollywood Royale</h1> */}
                    </div>

                    <div className="bottom-nav">
                        <a href="#" className="bottom-pill">Luxury Living</a>
                        <a href="#" className="bottom-pill">Prime Location</a>
                        <a href="#" className="bottom-pill">
                            Premium Amenities
                            <svg className="sparkle" viewBox="0 0 24 24" style={{ marginLeft: '8px', width: '18px', height: '18px', fill: 'white' }}>
                                <path d="M12,2 L15,9 L22,12 L15,15 L12,22 L9,15 L2,12 L9,9 Z"></path>
                            </svg>
                        </a>
                    </div>
                </main>
            </div>

            {/* Senior Living Section */}
            <SeniorLivingSection />

            {/* Why Choose Us Section */}
            <div style={{ contentVisibility: 'auto' }}>
                <WhyChooseUs />
            </div>


            {/* Horizontal Scroll Section - Community Showcase */}
            <div id="gallery" className="horizontal-section" style={{ position: 'relative', overflow: 'hidden' }}>
                <div
                    className={`horizontal-wrapper no-scrollbar ${isDragging ? 'grabbing' : 'grab'}`}
                    ref={horizontalWrapper1Ref}
                    onMouseDown={handleMouseDown}
                    onMouseLeave={handleMouseLeave}
                    onMouseUp={handleMouseUp}
                    onMouseMove={handleMouseMoveDrag}
                >
                    <div className="horizontal-card" onClick={() => openImageModal('/IMG_7472.jpg', 'Hollywood Royale Garden', 'A beautiful assisted living community in the heart of Hollywood.')}>
                        <img src="/IMG_7472.jpg" alt="Aerial View of Hollywood Royale Garden" loading="lazy" width="800" height="533" />
                        <div className="horizontal-content">
                            <span className="content-label">Our Community</span>
                            <h3>Hollywood Royale Garden</h3>
                            <p>A beautiful assisted living community in the heart of Hollywood, designed to feel like home.</p>
                        </div>
                    </div>

                    <div className="horizontal-card" onClick={() => openImageModal('/IMG_8979.jpg', 'Comfortable Bedrooms', 'Private and shared rooms designed for comfort and relaxation.')}>
                        <img src="/IMG_8979.jpg" alt="Comfortable Bedroom at Hollywood Royale" loading="lazy" width="800" height="533" />
                        <div className="horizontal-content">
                            <span className="content-label">Private Rooms</span>
                            <h3>Comfortable Bedrooms</h3>
                            <p>Private and shared rooms designed for comfort and relaxation, with lovely views.</p>
                        </div>
                    </div>

                    <div className="horizontal-card" onClick={() => openImageModal('/IMG_9046.jpg', 'Spacious Living Rooms', 'Welcoming common areas where residents gather and socialize.')}>
                        <img src="/IMG_9046.jpg" alt="Spacious Living Room and Common Area" loading="lazy" width="800" height="533" />
                        <div className="horizontal-content">
                            <span className="content-label">Common Areas</span>
                            <h3>Spacious Living Rooms</h3>
                            <p>Welcoming common areas where residents gather, socialize, and enjoy activities together.</p>
                        </div>
                    </div>

                    <HorizontalAmenities />
                </div>

                {/* Custom Scroll Arrows */}
                <button className="scroll-arrow-btn left" onClick={scrollLeft} aria-label="Scroll Left">
                    <ChevronLeft size={32} />
                </button>
                <button className="scroll-arrow-btn right" onClick={scrollRight} aria-label="Scroll Right">
                    <ChevronRight size={32} />
                </button>
            </div>


            {/* Contact Us Section */}
            <section id="contact">
                <ContactUs />
            </section>

            {/* Footer */}
            <Footer />

            {/* Image Zoom Modal */}
            {modalImage && (
                <div
                    className="image-modal active"
                    onClick={closeImageModal}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(0,0,0,0.9)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        cursor: 'zoom-out'
                    }}
                >
                    <div
                        className="image-modal-content"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'relative',
                            maxWidth: '95%',
                            maxHeight: '95%',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            overflow: 'hidden'
                        }}
                    >
                        <div className="modal-image-wrapper" style={{ position: 'relative', flex: 1, minHeight: 0, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
                            <div style={{ position: 'relative', display: 'inline-flex', maxWidth: '100%', maxHeight: '100%', justifyContent: 'center', alignItems: 'center' }}>
                                <button className="modal-close-btn" onClick={closeImageModal} style={{ position: 'absolute', top: 10, right: 10, zIndex: 50, background: 'rgba(0,0,0,0.5)', borderRadius: '50%', padding: '5px', border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px' }}>
                                    <X size={18} color="white" />
                                </button>
                                {modalImage && (
                                    <img src={modalImage} alt={modalInfo.title} style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', display: 'block', borderRadius: '10px', objectFit: 'contain' }} />
                                )}
                            </div>
                        </div>
                        <div className="md:hidden" style={{ color: 'white', marginTop: '15px', textAlign: 'center', flexShrink: 0, paddingBottom: '10px' }}>
                            <h3 style={{ fontSize: '20px', marginBottom: '5px' }}>{modalInfo.title}</h3>
                            <p style={{ fontSize: '14px', lineHeight: '1.4' }}>{modalInfo.description}</p>
                        </div>
                    </div>
                </div>
            )}
        </SmoothScroll>
    );
}

export default Home;
